import type { FocusSession, SessionMode } from '../types'
import { createId } from './id'

export function createSession(title: string, minutes: number, mode: SessionMode): FocusSession {
  const startedAt = Date.now()
  return {
    id: createId(),
    title: title.trim() || 'Open focus',
    plannedMinutes: minutes,
    activeSeconds: 0,
    pauseSeconds: 0,
    mode,
    status: 'running',
    startedAt,
    plannedEndAt: startedAt + minutes * 60_000,
    accumulatedPauseMs: 0,
    rewarded: false,
    feedEarned: 0,
    marksEarned: 0,
  }
}

export function elapsedMs(session: FocusSession, now = Date.now()): number {
  const pauseInProgress = session.status === 'paused' && session.pausedAt ? now - session.pausedAt : 0
  return Math.max(0, now - session.startedAt - session.accumulatedPauseMs - pauseInProgress)
}

export function remainingSeconds(session: FocusSession, now = Date.now()): number {
  return Math.max(0, Math.ceil((session.plannedMinutes * 60_000 - elapsedMs(session, now)) / 1000))
}

export function pauseSession(session: FocusSession, now = Date.now()): FocusSession {
  if (session.status !== 'running') return session
  return { ...session, status: 'paused', pausedAt: now }
}

export function resumeSession(session: FocusSession, now = Date.now()): FocusSession {
  if (session.status !== 'paused' || !session.pausedAt) return session
  const pauseMs = Math.max(0, now - session.pausedAt)
  return {
    ...session,
    status: 'running',
    accumulatedPauseMs: session.accumulatedPauseMs + pauseMs,
    plannedEndAt: session.plannedEndAt + pauseMs,
    pausedAt: undefined,
    pauseSeconds: Math.floor((session.accumulatedPauseMs + pauseMs) / 1000),
  }
}

export function completeSession(session: FocusSession, now = Date.now()): FocusSession {
  return {
    ...session,
    status: 'completed',
    completedAt: now,
    activeSeconds: session.plannedMinutes * 60,
    pausedAt: undefined,
  }
}

export function endSessionEarly(session: FocusSession, now = Date.now()): FocusSession {
  return {
    ...session,
    status: 'ended_early',
    completedAt: now,
    activeSeconds: Math.floor(elapsedMs(session, now) / 1000),
    pausedAt: undefined,
  }
}

export function formatClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const remainder = seconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}`
}
